import React, { useMemo, useState } from "react";
import { CmdIcon } from "./shared/CmdIcon";
import { useProjectTaskStats } from "../hooks/useProjectTaskStats";
import { formatClientCurrency } from "../helpers/clientIntelligence";
import { formatInvoiceStatus, resolveInvoiceStatus } from "../helpers/invoices";
import { paymentStateLabel, summarizeInvoiceSettlement } from "../helpers/invoiceMoney";
import { isClientArchived } from "../helpers/clientVisibility";
import { formatTaskDate } from "../helpers/tasks/taskModel";
import "./project/projectDesktopOverview.css";
import "./clientDesktopDetail.css";

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return `${parts[0][0]}${parts.length > 1 ? parts[parts.length - 1][0] : ""}`.toUpperCase();
}

function dateLabel(value) {
  const day = String(value || "").slice(0, 10);
  return formatTaskDate(day, { year: true }) || "—";
}

function relative(value) {
  const t = new Date(value).getTime();
  if (!Number.isFinite(t)) return "";
  const days = Math.floor((Date.now() - t) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 14) return `${days} days ago`;
  return new Date(t).toLocaleDateString();
}

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "projects", label: "Projects" },
  { id: "invoices", label: "Invoices" },
  { id: "activity", label: "Activity" },
  { id: "contacts", label: "Contacts" },
  { id: "notes", label: "Notes" },
];

/**
 * Desktop Client detail (concept CP-DSK-CLI-DTL-v01). Composition over the
 * existing Client Intelligence snapshot — no new metrics are computed here
 * beyond counts of rows already loaded.
 */
export function ClientDesktopDetail({
  client,
  view,
  canEdit,
  onEditClient,
  onViewProjects,
  onOpenTimeLogs,
  onOpenProject,
  onOpenInvoice,
  onOpenStatement,
}) {
  const [tab, setTab] = useState("overview");
  const [showCompleted, setShowCompleted] = useState(false);
  const taskStats = useProjectTaskStats({ enabled: true });
  const archived = isClientArchived(client);
  const m = view.metrics;
  const financial = view.flags.includeFinancial;
  const addressLine = [client?.address, client?.city, client?.province, client?.postal_code].filter(Boolean).join(", ");
  const projectName = useMemo(() => new Map(view.projects.all.map((p) => [p.projectId, p.name])), [view.projects.all]);

  const projects = showCompleted ? view.projects.all : view.projects.active;
  const invoiceRows = [...view.invoices.open, ...view.invoices.recent].slice(0, 8);
  const totalProjects = view.projects.all.length;
  const show = (id) => tab === "overview" || tab === id;

  return (
    <div className="pdo cdd" data-testid="client-desktop-detail">
      <header className="cdd-head">
        <span className="cdd-head__avatar" aria-hidden="true">{initials(client?.client_name)}</span>
        <div className="cdd-head__main">
          <div className="cdd-head__name">
            <h1>{client?.client_name || "Unnamed Client"}</h1>
            <span className={`pj-status${archived ? " pj-status--archived" : ""}`}>{archived ? "Archived" : "Active"}</span>
          </div>
          <ul className="cdd-head__lines">
            {client?.phone ? <li><CmdIcon name="phone" />{client.phone}</li> : null}
            {client?.email ? <li><CmdIcon name="mail" />{client.email}</li> : null}
            {addressLine ? <li><CmdIcon name="pin" />{addressLine}</li> : null}
          </ul>
          <div className="cdd-head__actions">
            {canEdit ? <button type="button" className="cdd-btn" onClick={onEditClient}><CmdIcon name="edit" /> Edit Client</button> : null}
            <button type="button" className="cdd-btn" onClick={onViewProjects}><CmdIcon name="folder" /> View Projects</button>
          </div>
        </div>
        <div className="cdd-kpis">
          <div className="cdd-kpi"><span className="cdd-kpi__icon"><CmdIcon name="folder" /></span><b>{totalProjects}</b><span>Total Projects</span></div>
          <div className="cdd-kpi cdd-kpi--green"><span className="cdd-kpi__icon"><CmdIcon name="dollar" /></span><b>{m.activeProjectValue == null ? "—" : formatClientCurrency(m.activeProjectValue).replace(/\.00$/, "")}</b><span>Active Project Value</span></div>
          <div className="cdd-kpi cdd-kpi--blue"><span className="cdd-kpi__icon"><CmdIcon name="check" /></span><b>{m.activeProjectCount}</b><span>Active Projects</span></div>
          {financial ? (
            <div className="cdd-kpi cdd-kpi--gold"><span className="cdd-kpi__icon"><CmdIcon name="doc" /></span><b>{m.openInvoiceCount}</b><span>Open Invoices</span></div>
          ) : m.totalHours != null ? (
            <button type="button" className="cdd-kpi cdd-kpi--gold" onClick={onOpenTimeLogs}><span className="cdd-kpi__icon"><CmdIcon name="clock" /></span><b>{Number(m.totalHours).toFixed(1)}</b><span>Total Hours</span></button>
          ) : null}
        </div>
      </header>

      <nav className="pdo-tabs" aria-label="Client sections">
        {TABS.filter((t) => t.id !== "invoices" || financial).map((t) => (
          <button key={t.id} type="button" className={`pdo-tab${tab === t.id ? " is-on" : ""}`} aria-current={tab === t.id ? "page" : undefined} onClick={() => setTab(t.id)}>
            {t.label}
            {t.id === "projects" ? <span className="pdo-tab__count">{totalProjects}</span> : null}
            {t.id === "invoices" && financial ? <span className="pdo-tab__count">{m.openInvoiceCount + m.paidInvoiceCount}</span> : null}
          </button>
        ))}
      </nav>

      <div className="pdo-cols">
        <div className="pdo-main">
          {show("projects") ? (
            <section className="pdo-card" data-testid="client-intel-projects">
              <h2 className="pdo-card__title"><span className="pdo-card__icon"><CmdIcon name="folder" /></span>{showCompleted ? "Projects" : "Active Projects"}
                {view.projects.completed.length > 0 ? (
                  <button type="button" className="pdo-link" onClick={() => setShowCompleted((v) => !v)}>{showCompleted ? "Hide completed" : "Show completed projects"}</button>
                ) : null}
              </h2>
              {projects.length === 0 ? (
                <p className="pdo-empty">No active projects for this client.</p>
              ) : (
                <div className="cdd-projects">
                  {projects.map((project) => {
                    const stats = taskStats?.byProject.get(project.projectId);
                    const pct = stats && stats.total ? Math.round((stats.completed / stats.total) * 100) : null;
                    return (
                      <button key={project.projectId} type="button" className="cdd-project" onClick={() => onOpenProject?.(project.raw || project)}>
                        <span className="cdd-project__img"><CmdIcon name="folder" /></span>
                        <span className="cdd-project__name">{project.name}</span>
                        <span className="cdd-project__addr">{project.siteAddress || (project.projectNumber ? `#${project.projectNumber}` : "No site address")}</span>
                        <span className={`pj-status${project.isActive ? "" : " pj-status--archived"}`}>{project.isActive ? "Active" : "Completed"}</span>
                        <span className="cdd-project__prog">
                          <span className="cdd-project__bar"><span style={{ width: `${pct ?? 0}%` }} /></span>
                          <em>{pct == null ? "—" : `${pct}%`}</em>
                        </span>
                        <span className="cdd-project__tasks">{stats ? `${stats.completed} / ${stats.total} tasks` : "Task progress unavailable"}</span>
                        <span className="cdd-project__foot">
                          <span><i>Target</i>{project.raw?.estimated_end_date ? dateLabel(project.raw.estimated_end_date) : "Not set"}</span>
                          <span><i>Value</i>{project.value == null ? "—" : formatClientCurrency(project.value).replace(/\.00$/, "")}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          ) : null}

          {financial && (show("invoices") || tab === "overview") ? (
            <div className="cdd-pair">
              <section className="pdo-card" data-testid="client-intel-financial">
                <h2 className="pdo-card__title"><span className="pdo-card__icon"><CmdIcon name="dollar" /></span>Financial Summary</h2>
                {typeof onOpenStatement === "function" ? (
                  <button type="button" className="cdd-btn" onClick={onOpenStatement}>
                    Statement of Account
                  </button>
                ) : null}
                <div className="cdd-tiles">
                  <div className="cdd-tile"><b>{m.activeProjectValue == null ? "—" : formatClientCurrency(m.activeProjectValue).replace(/\.00$/, "")}</b><span>Active Project Value</span></div>
                  <div className="cdd-tile cdd-tile--gold"><b>{formatClientCurrency(m.outstandingBalance).replace(/\.00$/, "")}</b><span>Outstanding Balance</span></div>
                  <div className="cdd-tile cdd-tile--red"><b>{formatClientCurrency(m.overdueAmount).replace(/\.00$/, "")}</b><span>Overdue Amount</span></div>
                </div>
              </section>
              <section className="pdo-card">
                <h2 className="pdo-card__title"><span className="pdo-card__icon"><CmdIcon name="doc" /></span>Invoice Status</h2>
                <div className="cdd-tiles cdd-tiles--4">
                  <div className="cdd-tile"><b>{m.openInvoiceCount + m.paidInvoiceCount}</b><span>Total Invoices</span></div>
                  <div className="cdd-tile cdd-tile--blue"><b>{m.paidInvoiceCount}</b><span>Paid</span></div>
                  <div className="cdd-tile cdd-tile--gold"><b>{m.openInvoiceCount}</b><span>Outstanding</span></div>
                  <div className="cdd-tile cdd-tile--red"><b>{m.overdueInvoiceCount}</b><span>Overdue</span></div>
                </div>
              </section>
            </div>
          ) : null}

          {financial && show("invoices") ? (
            <section className="pdo-card">
              <h2 className="pdo-card__title"><span className="pdo-card__icon"><CmdIcon name="doc" /></span>Recent Invoices</h2>
              {invoiceRows.length ? (
                <table className="cdd-table">
                  <thead><tr><th>Invoice #</th><th>Project</th><th>Issue Date</th><th>Due Date</th><th className="is-num">Amount</th><th>Status</th></tr></thead>
                  <tbody>
                    {invoiceRows.map((inv) => {
                      const status = resolveInvoiceStatus(inv.status);
                      const issued = status === "sent" || status === "paid";
                      const label = issued
                        ? paymentStateLabel(summarizeInvoiceSettlement(inv).state)
                        : formatInvoiceStatus(inv.status);
                      const tone = label === "Paid" ? "green" : "gold";
                      return (
                        <tr key={inv.invoice_id} onClick={() => onOpenInvoice?.(inv)} tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onOpenInvoice?.(inv)}>
                          <td>{inv.invoice_number || "Invoice"}</td>
                          <td>{projectName.get(inv.project_id) || "—"}</td>
                          <td>{inv.sent_at ? dateLabel(inv.sent_at) : "—"}</td>
                          <td>{inv.dueDate ? dateLabel(inv.dueDate) : "—"}</td>
                          <td className="is-num">{formatClientCurrency(inv.total_amount)}</td>
                          <td><span className={`cdd-pill cdd-pill--${tone}`}>{label}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <p className="pdo-empty">{m.hasInvoiceHistory ? "No recent invoices." : "No invoices on file for this client yet."}</p>
              )}
            </section>
          ) : null}

          {tab === "overview" || tab === "notes" ? (
            <div className="cdd-pair">
              <section className="pdo-card">
                <h2 className="pdo-card__title"><span className="pdo-card__icon"><CmdIcon name="doc" /></span>Notes</h2>
                {client?.notes ? <p className="pdo-desc cdd-notes">{client.notes}</p> : <p className="pdo-empty">No notes for this client yet.</p>}
              </section>
              <section className="pdo-card">
                <h2 className="pdo-card__title"><span className="pdo-card__icon"><CmdIcon name="box" /></span>Documents</h2>
                <p className="pdo-empty">Client documents aren’t available yet.</p>
              </section>
            </div>
          ) : null}

          {show("activity") && tab !== "overview" ? (
            <section className="pdo-card" data-testid="client-intel-activity">
              <h2 className="pdo-card__title"><span className="pdo-card__icon"><CmdIcon name="pulse" /></span>Recent Activity</h2>
              {view.recentActivity.length ? (
                <ul className="pdo-feed">
                  {view.recentActivity.map((item) => (
                    <li key={`${item.type}-${item.id}-${item.at}`}>
                      <span className="pdo-feed__icon"><CmdIcon name={item.type === "invoice" ? "doc" : item.type === "time" ? "clock" : "folder"} /></span>
                      <span className="pdo-feed__text"><b>{item.detail}</b><span>{item.label}</span></span>
                      <span className="pdo-feed__time">{relative(item.at)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="pdo-empty">No recent project, invoice, or time activity for this client.</p>
              )}
            </section>
          ) : null}
        </div>

        <aside className="pdo-rail">
          <section className="pdo-card">
            <h2 className="pdo-card__title"><span className="pdo-card__icon"><CmdIcon name="user" /></span>Client Information
              {canEdit ? <button type="button" className="pdo-link" onClick={onEditClient}>Edit</button> : null}</h2>
            <dl className="pdo-kv">
              <div><dt>Name</dt><dd>{client?.client_name || "—"}</dd></div>
              <div><dt>Phone</dt><dd>{client?.phone || "—"}</dd></div>
              <div><dt>Email</dt><dd>{client?.email || "—"}</dd></div>
              <div><dt>Address</dt><dd>{addressLine || "—"}</dd></div>
              <div><dt>Status</dt><dd><span className={`pj-status${archived ? " pj-status--archived" : ""}`}>{archived ? "Archived" : "Active"}</span></dd></div>
              <div><dt>Client Since</dt><dd>{client?.created_at ? dateLabel(client.created_at) : "—"}</dd></div>
            </dl>
          </section>

          {show("contacts") ? (
            <section className="pdo-card">
              <h2 className="pdo-card__title"><span className="pdo-card__icon"><CmdIcon name="users" /></span>Contacts</h2>
              {client?.contact_name || client?.secondary_phone ? (
                <ul className="pdo-team">
                  {client?.contact_name ? (
                    <li><span className="pdo-team__avatar">{initials(client.contact_name)}</span><span className="pdo-team__name">{client.contact_name}<small>{[client.phone, client.email].filter(Boolean).join(" · ")}</small></span><span className="pdo-team__count">Primary</span></li>
                  ) : null}
                  {client?.secondary_phone ? (
                    <li><span className="pdo-team__avatar"><CmdIcon name="phone" /></span><span className="pdo-team__name">Secondary phone<small>{client.secondary_phone}</small></span></li>
                  ) : null}
                </ul>
              ) : (
                <p className="pdo-empty">No contacts on file.</p>
              )}
            </section>
          ) : null}

          {tab === "overview" ? (
            <section className="pdo-card" data-testid="client-intel-activity-rail">
              <h2 className="pdo-card__title"><span className="pdo-card__icon"><CmdIcon name="pulse" /></span>Recent Activity</h2>
              {view.recentActivity.length ? (
                <ul className="pdo-feed">
                  {view.recentActivity.slice(0, 5).map((item) => (
                    <li key={`${item.type}-${item.id}-${item.at}`}>
                      <span className="pdo-feed__icon"><CmdIcon name={item.type === "invoice" ? "doc" : item.type === "time" ? "clock" : "folder"} /></span>
                      <span className="pdo-feed__text"><b>{item.detail}</b><span>{item.label}</span></span>
                      <span className="pdo-feed__time">{relative(item.at)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="pdo-empty">No recent activity for this client.</p>
              )}
            </section>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
